import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { PaginatedService } from '../model/pagination/paginated-service.model';
import { Observable } from 'rxjs';
import { PaginatedResponse } from '../model/pagination/paginated-response.model';
import { map } from 'rxjs/operators';
import { Organization } from '../model/organization/organization.model';
import { HttpOptions } from '../model/http-options.model';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class OrganizationService implements PaginatedService<Organization> {
  private httpClient = inject(HttpClient);

  public getPaginatedList(options: HttpOptions = {}): Observable<PaginatedResponse<Organization>> {
    return this.httpClient.get(
      `${environment.apiUrl}/api/organizations`,
      options,
    ).pipe(
      map((response: any): PaginatedResponse<Organization> => {
        const items: Organization[] = response.items
          .map((response: any): Organization => Organization.createFromApi(response));
        return new PaginatedResponse<Organization>({
          ...response,
          items,
        });
      }),
    );
  }

  public overview(options: HttpOptions = {}): Observable<Organization[]> {
    return this.httpClient.get(
      `${environment.apiUrl}/api/map/organizations`,
      options,
    ).pipe(
      map((responseList: any): Organization[] => (responseList ?? []).map((response: any): Organization => Organization.createFromApi({
        ...response,
        headerImage: {id: response.headerImageId},
        area: {name: response.area},
        thema: {name: response.thema},
      }))),
    );
  }

  public search(search: string): Observable<Organization[]> {
    return this.httpClient.get(
      `${environment.apiUrl}/api/organizations`,
      {
        params: {
          search,
          pageSize: 50_000,
        },
      },
    ).pipe(
      map((response: any): Organization[] => {
        return response.items
          .map((response: any): Organization => Organization.createFromApi(response));
      }),
    );
  }

  public getOrganizationById(collectionId: string): Observable<Organization> {
    return this.httpClient.get(`${environment.apiUrl}/api/organizations/${collectionId}`)
      .pipe(
        map((response: any): Organization => Organization.createFromApi(response)),
      );
  }

  public getOrganizationBySlug(slug: string): Observable<Organization> {
    return this.httpClient.get(`${environment.apiUrl}/api/map/organizations/${slug}`)
      .pipe(
        map((response: any): Organization => Organization.createFromApi(response)),
      );
  }

  public getOrganizationOpenLinkedDataBydId(organizationId: string): Observable<any> {
    return this.httpClient.get(`${environment.apiUrl}/api/map/organizations/${organizationId}/json-ld`);
  }

  public getOrganizationOpenLinkedDataBydCollectionId(collectionId: string): Observable<any> {
    return this.httpClient.get(`${environment.apiUrl}/api/map/organizations/${collectionId}/json-ld`);
  }

  public createOrganization(organization: Organization, publish: boolean): Observable<{ id: string }> {
    return this.httpClient.post<{ id: string }>(
      `${environment.apiUrl}/api/organizations`,
      organization.processForApi(publish),
    );
  }

  public updateOrganization(organization: Organization, publish: boolean): Observable<void> {
    return this.httpClient.put<void>(
      `${environment.apiUrl}/api/organizations/${organization.id}`,
      organization.processForApi(publish),
    );
  }

  public deleteOrganization(organizationId: string): Observable<void> {
    return this.httpClient.delete<void>(`${environment.apiUrl}/api/organizations/${organizationId}`);
  }
}
